import { useState, useCallback, useRef, useEffect } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

export function useChatWebSocket(token, onMessage) {
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const isConnectingRef = useRef(false);
  const onMessageRef = useRef(onMessage);
  const tokenRef = useRef(token);
  const lastActivityRef = useRef(Date.now());

  // Keep refs in sync
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    const currentToken = tokenRef.current;
    
    // Prevent multiple simultaneous connection attempts
    if (!currentToken || isConnectingRef.current) {
      return;
    }

    // Check if already connected or connecting
    const existingSocket = socketRef.current;
    if (existingSocket) {
      if (existingSocket.readyState === WebSocket.OPEN) {
        setIsConnected(true);
        return;
      }
      if (existingSocket.readyState === WebSocket.CONNECTING) {
        return;
      }
      // Close any existing socket that's not open or connecting
      try {
        existingSocket.close(1000, 'Reconnecting');
      } catch (e) {
        // Ignore close errors
      }
    }

    isConnectingRef.current = true;
    console.log('Connecting to WebSocket...');

    const wsUrl = API.replace('https://', 'wss://').replace('http://', 'ws://');
    
    try {
      const ws = new WebSocket(`${wsUrl}/api/ws/chat?token=${currentToken}`);
      socketRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected');
        isConnectingRef.current = false;
        reconnectAttempts.current = 0;
        lastActivityRef.current = Date.now();
        setIsConnected(true);
      };

      ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        isConnectingRef.current = false;
        setIsConnected(false);
        
        // Only reconnect if not intentional close and we have a token
        if (event.code !== 1000 && tokenRef.current) {
          scheduleReconnect();
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        isConnectingRef.current = false;
      };

      ws.onmessage = (event) => {
        lastActivityRef.current = Date.now();
        try {
          const data = JSON.parse(event.data);
          if (onMessageRef.current) {
            onMessageRef.current(data);
          }
        } catch (e) {
          console.error('Error parsing WebSocket message:', e);
        }
      };
    } catch (error) {
      console.error('Error creating WebSocket:', error);
      isConnectingRef.current = false;
    }
  }, []); // No dependencies - uses refs

  // Schedule reconnection with exponential backoff
  const scheduleReconnect = useCallback(() => {
    // Exponential backoff with max 30 seconds (2 min while asleep in the background — push covers calls/messages)
    const asleep = window.__bbcAsleep && window.__bbcAsleep();
    const backoff = asleep ? 120000 : Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
    reconnectAttempts.current++;
    
    console.log(`Scheduling reconnect in ${backoff}ms (attempt ${reconnectAttempts.current})`);
    
    // Clear any existing timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    
    reconnectTimeoutRef.current = setTimeout(() => {
      if (tokenRef.current) {
        connectWebSocket();
      }
    }, backoff);
  }, [connectWebSocket]);

  // Force reconnect (used when page becomes visible)
  const forceReconnect = useCallback(() => {
    console.log('Force reconnecting WebSocket...');
    reconnectAttempts.current = 0; // Reset backoff
    
    // Close existing socket if any
    if (socketRef.current) {
      try {
        socketRef.current.close(1000, 'Force reconnect');
      } catch (e) {
        // Ignore
      }
      socketRef.current = null;
    }
    
    isConnectingRef.current = false;
    setIsConnected(false);
    
    // Reconnect immediately
    if (tokenRef.current) {
      connectWebSocket();
    }
  }, [connectWebSocket]);

  // Send WebSocket message with HTTP fallback for offline
  const sendWsMessage = useCallback(async (data) => {
    const ws = socketRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify(data));
        lastActivityRef.current = Date.now();
        return true;
      } catch (e) {
        console.error('Error sending WebSocket message:', e);
      }
    }
    
    // HTTP fallback for messages when WebSocket is not connected
    if (data.type === 'message' && data.conversation_id && (data.content || data.file_url)) {
      console.log('WebSocket not connected, sending via HTTP API');
      try {
        const { type, conversation_id, ...msgPayload } = data;
        const response = await fetch(`${API}/api/chat/conversations/${conversation_id}/messages`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokenRef.current}`
          },
          body: JSON.stringify(msgPayload)
        });
        
        if (response.ok) {
          const messageData = await response.json();
          // Trigger the onMessage callback to update UI
          if (onMessageRef.current) {
            onMessageRef.current({
              type: 'new_message',
              message: messageData
            });
          }
          return true;
        } else {
          console.error('HTTP message send failed:', response.status);
          return false;
        }
      } catch (error) {
        console.error('HTTP message send error:', error);
        return false;
      }
    }
    
    console.warn('WebSocket not connected, message not sent:', data.type);
    return false;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (socketRef.current) {
        try {
          socketRef.current.close(1000, 'Component unmounting');
        } catch (e) {
          // Ignore close errors
        }
        socketRef.current = null;
      }
    };
  }, []);

  // Heartbeat to keep connection alive - 15 s in the foreground, ~60 s while the app is asleep in the background
  useEffect(() => {
    if (!isConnected) return;
    
    let lastPing = 0;
    const beat = () => {
      const asleep = window.__bbcAsleep && window.__bbcAsleep();
      if (asleep && Date.now() - lastPing < 60000) return;
      lastPing = Date.now();
      const ws = socketRef.current;
      if (ws?.readyState === WebSocket.OPEN) {
        try {
          ws.send(JSON.stringify({ type: 'ping' }));
        } catch (e) {
          console.error('Heartbeat failed:', e);
          // Trigger reconnect on heartbeat failure
          forceReconnect();
        }
      } else if (!asleep) {
        // WebSocket not open, trigger reconnect (while asleep the visibility handler reconnects on reopen)
        forceReconnect();
      }
    };
    beat.__bbcEssential = true;
    const interval = setInterval(beat, 15000); // Every 15 seconds
    
    return () => clearInterval(interval);
  }, [isConnected, forceReconnect]);

  // Handle visibility change - reconnect when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('Page became visible, checking WebSocket connection...');
        
        const ws = socketRef.current;
        const timeSinceLastActivity = Date.now() - lastActivityRef.current;
        
        // If more than 30 seconds since last activity or socket is not open, reconnect
        if (!ws || ws.readyState !== WebSocket.OPEN || timeSinceLastActivity > 30000) {
          console.log('WebSocket needs reconnection after visibility change');
          forceReconnect();
        } else {
          // Send a ping to verify connection is still alive
          try {
            ws.send(JSON.stringify({ type: 'ping' }));
          } catch (e) {
            console.log('Ping failed, reconnecting...');
            forceReconnect();
          }
        }
      }
    };

    // Handle online/offline events
    const handleOnline = () => {
      console.log('Network came online, reconnecting WebSocket...');
      forceReconnect();
    };

    const handleOffline = () => {
      console.log('Network went offline');
      setIsConnected(false);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Also handle page focus (for some browsers)
    window.addEventListener('focus', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, [forceReconnect]);

  // Connect when token changes
  useEffect(() => {
    if (token) {
      // Small delay to prevent race conditions during auth
      const timeout = setTimeout(() => {
        connectWebSocket();
      }, 100);
      return () => clearTimeout(timeout);
    } else {
      // Disconnect when token is removed
      if (socketRef.current) {
        socketRef.current.close(1000, 'Logged out');
        socketRef.current = null;
      }
      setIsConnected(false);
    }
  }, [token, connectWebSocket]);

  return {
    isConnected,
    connectWebSocket,
    forceReconnect,
    sendWsMessage,
    socket: socketRef.current
  };
}
